/**
 * Phase 32 Plan 32-2 — engineering-metrics frontend API client.
 *
 * Endpoints (Plan 32-2 backend):
 *   GET /api/engineering-metrics/leaderboard?window=&projectId=
 *   GET /api/engineering-metrics/users/:userId?window=&projectId=
 *   GET /api/engineering-metrics/automation-share?window=
 *   GET /api/admin/engineering-metrics/unmapped
 */
import { apiGet } from './client';

export type Window = '7d' | '30d' | '13w' | 'all';

/** Line-stat triple returned by the backend for additions, deletions, and files changed. */
export interface LineStats {
  total: number;
  human: number;
  aiAssisted: number;
}

export interface LeaderboardRow {
  userId: number;
  userName: string;
  total: number;
  aiPct: number; // 0..1
  perWeek: number[]; // 13 bins, oldest → newest
  additions: LineStats;
  deletions: LineStats;
  filesChanged: LineStats;
}

export interface PerRepoRow {
  repoId: string; // BigInt serialised as string
  repoName: string;
  total: number;
  aiPct: number;
}

export interface EngineerSeries {
  userId: number;
  userName: string;
  /** GitHub login from the engineer's User record. Null when not linked yet. */
  githubUsername: string | null;
  total: number;
  aiPct: number;
  perWeek: number[];
  perRepo: PerRepoRow[];
  additions: LineStats;
  deletions: LineStats;
  filesChanged: LineStats;
}

export interface AutomationShare {
  window: Window;
  automationCount: number;
  totalCount: number;
  share: number; // 0..1
}

export interface UnmappedRow {
  login: string;
  count: number;
  firstSeenAt: string; // ISO 8601
  latestCommit: {
    sha: string;
    committedAt: string;
    messagePreview: string;
  };
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (!entries.length) return '';
  return (
    '?' +
    entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')
  );
}

export async function fetchLeaderboard(input: {
  window: Window;
  projectId?: number;
}): Promise<LeaderboardRow[]> {
  const res = await apiGet<{ data: LeaderboardRow[] }>(
    `/engineering-metrics/leaderboard${qs({ window: input.window, projectId: input.projectId })}`,
  );
  return res.data;
}

export async function fetchEngineerSeries(
  userId: number,
  input: { window: Window; projectId?: number },
): Promise<EngineerSeries> {
  const res = await apiGet<{ data: EngineerSeries }>(
    `/engineering-metrics/users/${userId}${qs({ window: input.window, projectId: input.projectId })}`,
  );
  return res.data;
}

export async function fetchAutomationShare(window: Window): Promise<AutomationShare> {
  const res = await apiGet<{ data: AutomationShare }>(
    `/engineering-metrics/automation-share${qs({ window })}`,
  );
  return res.data;
}

export async function fetchUnmappedCommits(): Promise<UnmappedRow[]> {
  const res = await apiGet<{ data: UnmappedRow[] }>(`/admin/engineering-metrics/unmapped`);
  return res.data;
}
